import apiClient from '../utils/apiClient';

/**
 * API service - All API calls use the standardized apiClient
 * which handles errors, logging, and response formatting automatically
 */
export const api = {
  // Food Safety APIs
  checkFoodSafety: async (foodName, culture, language, userId) => {
    return await apiClient.post('/food/check', {
      foodName,
      culture,
      language,
      userId,
    });
  },
  
  searchFoods: async (query, culture, userId = null) => {
    const params = { query: query || '', culture };
    if (userId) {
      params.userId = userId;
    }
    return await apiClient.get('/food/search', { params });
  },
  
  getPersonalizedFoods: async (userId, culture) => {
    return await apiClient.get('/food/personalized', {
      params: { userId, culture },
    });
  },
  
  getFoodsForNutrient: async (userId, culture, nutrient) => {
    return await apiClient.get('/food/nutrient', {
      params: { userId, culture, nutrient },
    });
  },
  
  getAllFoods: async (culture, userId = null) => {
    const params = { query: '', culture };
    if (userId) {
      params.userId = userId;
    }
    return await apiClient.get('/food/search', { params });
  },
  
  getFoodInsights: async (culture, mode) => {
    return await apiClient.get('/food/insights', {
      params: { culture, mode },
    });
  },
  
  // Meal Planner APIs - NEW endpoint with mealType support
  getMealRecommendations: async (userId, mealType, userMood, filters, symptoms) => {
    const params = { mealType };
    if (userMood) params.userMood = userMood;
    if (filters && filters.length > 0) params.filters = filters;
    if (symptoms && symptoms.length > 0) params.symptoms = symptoms;
    return await apiClient.get(`/meal-planner/recommendations/${userId}`, { params });
  },
  
  // Legacy meal recommendations (for backward compatibility with old components)
  // This calls the OLD endpoint that returns a full meal plan
  getLegacyMealPlan: async (userId) => {
    return await apiClient.get(`/pregnancy/meals/${userId}`);
  },
  
  exploreMeals: async (userId, filter, symptoms) => {
    const params = { filter: filter || 'all' };
    if (symptoms && symptoms.length > 0) params.symptoms = symptoms;
    return await apiClient.get(`/meal-planner/explore/${userId}`, { params });
  },
  
  generateWeeklyPlan: async (userId, filters) => {
    return await apiClient.post(`/meal-planner/weekly-plan/${userId}`, { filters });
  },
  
  generateGroceryList: async (mealPlanDays) => {
    // Ensure we're sending the days array properly
    const payload = Array.isArray(mealPlanDays) ? { days: mealPlanDays } : { days: mealPlanDays?.days || [] };
    return await apiClient.post('/meal-planner/grocery-list', payload);
  },
  
  // getUserPreferences moved below to use correct endpoint /preferences/{userId}
  
  getRecentSymptoms: async (userId) => {
    // Use symptom history and filter to recent ones
    const history = await apiClient.get(`/symptom-checker/history/${userId}`);
    if (history?.data) {
      // Get symptoms from last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const recent = history.data
        .filter(log => {
          const logDate = new Date(log.logDate || log.date);
          return logDate >= sevenDaysAgo;
        })
        .flatMap(log => log.symptoms || [])
        .filter((s, i, arr) => arr.indexOf(s) === i); // unique
      
      return { data: recent };
    }
    return { data: [] };
  },
  
  // Food Items CRUD
  getAllFoodItems: async () => {
    return await apiClient.get('/food/items');
  },
  
  getFoodItem: async (id) => {
    return await apiClient.get(`/food/items/${id}`);
  },
  
  createFoodItem: async (foodItem) => {
    return await apiClient.post('/food/items', foodItem);
  },
  
  updateFoodItem: async (id, foodItem) => {
    return await apiClient.put(`/food/items/${id}`, foodItem);
  },
  
  deleteFoodItem: async (id) => {
    return await apiClient.delete(`/food/items/${id}`);
  },
  
  // Food History APIs
  getFoodHistory: async (userId) => {
    return await apiClient.get(`/history/user/${userId}`);
  },
  
  getAllHistory: async () => {
    return await apiClient.get('/history/all');
  },
  
  getHistoryItem: async (id) => {
    return await apiClient.get(`/history/${id}`);
  },
  
  createHistoryItem: async (historyItem) => {
    return await apiClient.post('/history', historyItem);
  },
  
  updateHistoryItem: async (id, historyItem) => {
    return await apiClient.put(`/history/${id}`, historyItem);
  },
  
  deleteHistoryItem: async (id) => {
    return await apiClient.delete(`/history/${id}`);
  },
  
  // Localization APIs
  getMessages: async (language) => {
    return await apiClient.get('/localization/messages', {
      params: { language },
    });
  },
  
  // Checklist APIs
  getWeeklyChecklist: async (userId, language) => {
    return await apiClient.get('/checklist/weekly', {
      params: { userId, language },
    });
  },
  
  toggleChecklistItem: async (userId, itemKey, completed) => {
    return await apiClient.post('/checklist/toggle', null, {
      params: { userId, itemKey, completed },
    });
  },
  
  getProgress: async (userId, language) => {
    return await apiClient.get('/checklist/progress', {
      params: { userId, language },
    });
  },
  
  // Pregnancy Tracking APIs
  savePregnancyProfile: async (profile) => {
    return await apiClient.post('/pregnancy/profile', profile);
  },
  
  getPregnancyProfile: async (userId) => {
    return await apiClient.get(`/pregnancy/profile/${userId}`, { params: { _t: Date.now() } });
  },
  
  getCurrentStatus: async (userId) => {
    return await apiClient.get(`/pregnancy/status/${userId}`);
  },
  
  getCurrentWeekAdvice: async (userId) => {
    return await apiClient.get(`/pregnancy/advice/current/${userId}`);
  },
  
  getWeekAdvice: async (week) => {
    return await apiClient.get(`/pregnancy/advice/week/${week}`);
  },
  
  getACOGAppointments: async (week) => {
    return await apiClient.get(`/pregnancy/acog/appointments/${week}`);
  },
  
  getACOGWarningSigns: async () => {
    return await apiClient.get(`/pregnancy/acog/warning-signs`);
  },
  
  calculateFromLMP: async (lmpDate) => {
    return await apiClient.post('/pregnancy/calculate/lmp', null, {
      params: { lmpDate },
    });
  },
  
  calculateFromEDD: async (eddDate) => {
    return await apiClient.post('/pregnancy/calculate/edd', null, {
      params: { eddDate },
    });
  },
  
  getExerciseGuide: async (trimester) => {
    return await apiClient.get(`/pregnancy/exercise/trimester/${trimester}`);
  },
  
  // Postpartum APIs
  getPostpartumInfo: async () => {
    return await apiClient.get('/pregnancy/postpartum/info');
  },
  
  getPostpartumAppointments: async (daysPostpartum) => {
    return await apiClient.get('/pregnancy/postpartum/appointments', {
      params: { daysPostpartum },
    });
  },
  
  getPostpartumWarningSigns: async () => {
    return await apiClient.get('/pregnancy/postpartum/warning-signs');
  },
  
  // Postpartum Tracking APIs
  savePostpartumCheckIn: async (userId, checkInData) => {
    return await apiClient.post('/postpartum/check-in', checkInData, {
      params: { userId },
    });
  },
  
  getPostpartumCheckIns: async (userId, days = 30) => {
    return await apiClient.get(`/postpartum/check-ins/${userId}`, {
      params: { days },
    });
  },
  
  savePostpartumRecoveryData: async (userId, recoveryData) => {
    return await apiClient.post('/postpartum/recovery', recoveryData, {
      params: { userId },
    });
  },
  
  getPostpartumRecoveryData: async (userId) => {
    return await apiClient.get(`/postpartum/recovery/${userId}`);
  },

  /** Premium: recovery phase and self-care tip (stage, weeksPostpartum, phaseLabelKey, selfCareTip). Returns null on 404. */
  getPostpartumRecoveryPhase: async () => {
    try {
      return await apiClient.get('/postpartum/recovery-phase');
    } catch (e) {
      if (e?.response?.status === 404 || e?.errorCode === 'NOT_APPLICABLE' || e?.errorCode === 'NOT_FOUND') return null;
      throw e;
    }
  },
  
  savePostpartumFeedingPreference: async (userId, feedingData) => {
    return await apiClient.post('/postpartum/feeding', feedingData, {
      params: { userId },
    });
  },
  
  getPostpartumFeedingPreference: async (userId) => {
    return await apiClient.get(`/postpartum/feeding/${userId}`);
  },
  
  savePostpartumSleepData: async (userId, sleepData) => {
    return await apiClient.post('/postpartum/sleep', sleepData, {
      params: { userId },
    });
  },
  
  getPostpartumSleepData: async (userId, days = 7) => {
    return await apiClient.get(`/postpartum/sleep/${userId}`, {
      params: { days },
    });
  },
  
  savePostpartumUserAppointment: async (userId, appointmentData) => {
    return await apiClient.post('/postpartum/appointments', appointmentData, {
      params: { userId },
    });
  },
  
  getPostpartumUserAppointments: async (userId) => {
    return await apiClient.get(`/postpartum/appointments/${userId}`);
  },
  
  deletePostpartumUserAppointment: async (appointmentId) => {
    return await apiClient.delete(`/postpartum/appointments/${appointmentId}`);
  },
  
  // User Preferences APIs
  saveUserPreferences: async (preferences) => {
    return await apiClient.post('/preferences', preferences);
  },
  
  getUserPreferences: async (userId) => {
    return await apiClient.get(`/preferences/${userId}`, { params: { _t: Date.now() } });
  },

  // Daily Tips
  getDailyTip: async (userId) => {
    return await apiClient.get(`/daily-tips/${userId}`);
  },

  // Kick Counter
  startKickSession: async (userId) => {
    return await apiClient.post(`/kick-counter/start/${userId}`);
  },
  addKick: async (sessionId, userId) => {
    return await apiClient.post(`/kick-counter/kick/${sessionId}`, { userId: String(userId) });
  },
  endKickSession: async (sessionId, userId, notes) => {
    return await apiClient.post(`/kick-counter/end/${sessionId}`, { 
      userId: String(userId), 
      notes: notes || '' 
    });
  },
  getKickSessions: async (userId) => {
    return await apiClient.get(`/kick-counter/sessions/${userId}`);
  },
  getKickSession: async (sessionId, userId) => {
    return await apiClient.get(`/kick-counter/session/${sessionId}/${userId}`);
  },

  // Contraction Timer
  startContraction: async (userId) => {
    return await apiClient.post(`/contraction-timer/start/${userId}`);
  },
  endContraction: async (timerId, userId) => {
    return await apiClient.post(`/contraction-timer/end/${timerId}`, { userId });
  },
  getContractions: async (userId) => {
    return await apiClient.get(`/contraction-timer/contractions/${userId}`);
  },
  getContraction: async (timerId, userId) => {
    return await apiClient.get(`/contraction-timer/contraction/${timerId}/${userId}`);
  },

  // Symptom Checker
  checkSymptoms: async (userId, symptoms) => {
    return await apiClient.post(`/symptom-checker/check/${userId}`, { symptoms });
  },
  getSymptomHistory: async (userId) => {
    return await apiClient.get(`/symptom-checker/history/${userId}`);
  },

  // Medication Safety
  checkMedication: async (medication) => {
    return await apiClient.get('/medication-safety/check', {
      params: { medication },
    });
  },
  
  getMedicationSuggestions: async (query) => {
    if (!query || query.trim().length < 2) {
      return [];
    }
    return await apiClient.get('/medication-safety/suggestions', {
      params: { query: query.trim() },
    });
  },

  // Local Services
  getAllLocalServices: async () => {
    return await apiClient.get('/local-services');
  },

  // Appointment Reminders
  createAppointmentReminder: async (reminder) => {
    return await apiClient.post('/appointment-reminders', reminder);
  },
  getAppointmentReminders: async (userId) => {
    return await apiClient.get(`/appointment-reminders/${userId}`);
  },
  getUserReminders: async (userId) => {
    return await apiClient.get(`/appointment-reminders/${userId}`);
  },
  getUpcomingReminders: async (userId) => {
    return await apiClient.get(`/appointment-reminders/upcoming/${userId}`);
  },
  getRecommendedAppointments: async (userId) => {
    return await apiClient.get(`/appointment-reminders/recommended/${userId}`);
  },
  generateAppointmentPlan: async (userId) => {
    // apiClient already extracts data from ApiResponse wrapper
    return await apiClient.get(`/appointment-reminders/plan/${userId}`);
  },
  markReminderCompleted: async (reminderId, userId) => {
    return await apiClient.post(`/appointment-reminders/complete/${reminderId}/${userId}`);
  },
  updateAppointmentStatus: async (reminderId, userId, status) => {
    return await apiClient.put(`/appointment-reminders/${reminderId}/${userId}/status`, { status });
  },
  updateReminder: async (reminderId, userId, reminder) => {
    return await apiClient.put(`/appointment-reminders/${reminderId}/${userId}`, reminder);
  },
  deleteReminder: async (reminderId, userId) => {
    return await apiClient.delete(`/appointment-reminders/${reminderId}/${userId}`);
  },
  
  // Authentication APIs
  register: async (email, password, firstName, lastName, phone, acceptedTerms = false) => {
    return await apiClient.post('/auth/register', {
      email,
      password,
      firstName,
      lastName,
      phone,
      acceptedTerms,
    });
  },
  
  login: async (email, password) => {
    return await apiClient.post('/auth/login', {
      email,
      password,
    });
  },
  
  verifyEmail: async (token) => {
    return await apiClient.post('/auth/verify-email', null, {
      params: { token },
    });
  },

  verifyEmailWithCode: async (email, code) => {
    return await apiClient.post('/auth/verify-email', null, {
      params: { email, code },
    });
  },

  resendEmailVerification: async (email) => {
    return await apiClient.post('/auth/resend-email-verification', null, {
      params: { email },
    });
  },
  
  verifyPhone: async (phone, code) => {
    return await apiClient.post('/auth/verify-phone', null, {
      params: { phone, code },
    });
  },
  
  resendPhoneVerification: async (phone) => {
    return await apiClient.post('/auth/resend-phone-verification', null, {
      params: { phone },
    });
  },
  
  // OAuth APIs
  getOAuthStatus: async () => {
    return await apiClient.get('/oauth/status');
  },
  
  oauthLogin: async (provider, providerId, email, firstName, lastName) => {
    return await apiClient.post(`/oauth/${provider.toLowerCase()}`, null, {
      params: {
        providerId,
        email,
        firstName,
        lastName,
      },
    });
  },
  
  // Billing APIs
  getSubscription: async () => {
    return await apiClient.get('/billing/subscription');
  },
  
  getSubscriptionStatus: async () => {
    return await apiClient.get('/billing/status');
  },
  
  createSubscription: async (plan) => {
    return await apiClient.post('/billing/subscribe', null, {
      params: { plan },
    });
  },
  
  cancelSubscription: async (cancelAtPeriodEnd = true) => {
    return await apiClient.post('/billing/cancel', null, {
      params: { cancelAtPeriodEnd },
    });
  },

  /** Get Stripe billing portal URL (restore purchases, update payment method). */
  trackFeatureUse: async (featureId) => {
    try {
      await apiClient.post('/analytics/feature-use', { featureId });
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Feature use track failed:', e?.message);
      }
    }
  },

  getBillingPortalUrl: async (returnUrl) => {
    const params = returnUrl ? { returnUrl } : {};
    const res = await apiClient.get('/billing/portal', { params });
    return res?.url ?? res?.data?.url;
  },

  // Account (GDPR: data export & delete)
  exportMyData: async () => {
    const res = await apiClient.get('/account/export');
    return res ?? res?.data;
  },
  deleteMyAccount: async () => {
    const res = await apiClient.delete('/account');
    return res;
  },

  // Journey Export (Premium: Export & Advanced Reports)
  getJourneyExportSummary: async () => {
    return await apiClient.get('/journey-export/summary');
  },

  getAppointmentPreparation: async (week) => {
    return await apiClient.get('/appointments/preparation', { params: { week } });
  },

  /** Premium: Pattern Intelligence – cycle variability, 3‑month change, luteal consistency */
  getCyclePatternInsights: async () => {
    return await apiClient.get('/cycle/pattern-insights');
  },

  /** Premium: Flow intensity trend (periods with flow for chart). */
  getFlowTrend: async () => {
    return await apiClient.get('/cycle/flow-trend');
  },

  /** Premium: Log period start with optional flow (LIGHT, MEDIUM, HEAVY). */
  logPeriod: async (periodStartDate, flowIntensity = null, flowNotes = null) => {
    const body = { periodStartDate: periodStartDate || new Date().toISOString().slice(0, 10) };
    if (flowIntensity) body.flowIntensity = flowIntensity;
    if (flowNotes) body.flowNotes = flowNotes;
    return await apiClient.post('/cycle/periods', body);
  },

  /** Premium: BBT biphasic shift detection. */
  getBbtBiphasic: async () => {
    return await apiClient.get('/cycle/bbt-biphasic');
  },

  /** Premium: Symptom clustering by phase + PMS pattern message. */
  getSymptomClustering: async () => {
    return await apiClient.get('/cycle/symptom-clustering');
  },

  /** Premium: Symptom heatmap calendar data (day × symptom intensity). */
  getSymptomHeatmap: async () => {
    return await apiClient.get('/cycle/symptom-heatmap');
  },

  getCycleLogs: async (from) => {
    const params = from ? { from } : {};
    return await apiClient.get('/cycle-logs', { params });
  },
  saveCycleLog: async (body) => {
    return await apiClient.post('/cycle-logs', body);
  },

  // Password Management
  requestPasswordReset: async (email) => {
    return await apiClient.post('/auth/forgot-password', null, {
      params: { email },
    });
  },
  
  resetPassword: async (token, newPassword) => {
    return await apiClient.post('/auth/reset-password', null, {
      params: { token, newPassword },
    });
  },
  
  changePassword: async (userId, currentPassword, newPassword) => {
    return await apiClient.post(`/auth/change-password?userId=${userId}`, {
      currentPassword,
      newPassword,
    });
  },
  
  // Profile Management
  updateProfile: async (userId, profileData) => {
    return await apiClient.put('/auth/profile', profileData, {
      params: { userId },
    });
  },
  
  getProfile: async (userId) => {
    return await apiClient.get('/auth/profile', {
      params: { userId, _t: Date.now() },
    });
  },
  
  // Admin APIs
  getAdminStats: async () => {
    return await apiClient.get('/admin/stats');
  },
  
  getAllUsers: async (search) => {
    return await apiClient.get('/admin/users', {
      params: { search: search || '' },
    });
  },
  
  updateUserRole: async (userId, role) => {
    return await apiClient.put(`/admin/users/${userId}/role`, null, {
      params: { role },
    });
  },

  updateUserMode: async (userId, payload) => {
    return await apiClient.put(`/admin/users/${userId}/mode`, payload);
  },

  deleteUser: async (userId) => {
    return await apiClient.delete(`/admin/users/${userId}`);
  },

  getUserActivity: async (userId) => {
    return await apiClient.get(`/admin/users/${userId}/activity`);
  },
  
  importFoodItems: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await apiClient.post('/admin/import/food', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  verifyItem: async (itemId, itemType) => {
    return await apiClient.post(`/admin/verify/${itemId}`, null, {
      params: { itemType },
    });
  },
  
  rejectItem: async (itemId, itemType, reason) => {
    return await apiClient.post(`/admin/reject/${itemId}`, null, {
      params: { itemType, reason: reason || '' },
    });
  },
  
  getPendingVerifications: async () => {
    return await apiClient.get('/admin/verifications/pending');
  },
  
  // Notification APIs
  getUserNotifications: async (userId) => {
    return await apiClient.get(`/notifications/user/${userId}`);
  },
  
  getUserNotificationsByType: async (userId, type) => {
    return await apiClient.get(`/notifications/user/${userId}/type/${type}`);
  },
  
  // Consent APIs
  getUserConsents: async (userId) => {
    return await apiClient.get(`/consent/user/${userId}`);
  },
  
  updateUserConsents: async (userId, consents) => {
    return await apiClient.put(`/consent/user/${userId}`, consents);
  },
  
  acceptTermsOfService: async (userId) => {
    return await apiClient.post(`/consent/user/${userId}/accept-terms`);
  },
  
  hasAcceptedTerms: async (userId) => {
    return await apiClient.get(`/consent/user/${userId}/terms-accepted`);
  },
  
  // Source Logging APIs
  logSourceAccess: async (logData) => {
    return await apiClient.post('/sources/log', logData);
  },
  
  getUserSourceLogs: async (userId) => {
    return await apiClient.get(`/sources/user/${userId}`);
  },
  
  getSourceStatistics: async () => {
    return await apiClient.get('/sources/statistics');
  },
  
  // Admin Week Advice APIs
  getAdminWeekAdviceAll: async () => {
    return await apiClient.get('/admin/week-advice/all');
  },
  
  getAdminWeekAdviceByWeek: async (weekNumber) => {
    return await apiClient.get(`/admin/week-advice/week/${weekNumber}`);
  },
  
  saveAdminWeekAdvice: async (content, editedBy) => {
    return await apiClient.post('/admin/week-advice/save', content, {
      params: { editedBy },
    });
  },
  
  publishAdminWeekAdvice: async (contentId, editedBy) => {
    return await apiClient.post(`/admin/week-advice/${contentId}/publish`, null, {
      params: { editedBy },
    });
  },
  
  unpublishAdminWeekAdvice: async (contentId, editedBy) => {
    return await apiClient.post(`/admin/week-advice/${contentId}/unpublish`, null, {
      params: { editedBy },
    });
  },
  
  // Admin Local Service APIs
  getAdminUnverifiedServices: async () => {
    return await apiClient.get('/admin/local-services/unverified');
  },
  
  getAdminVerifiedServices: async () => {
    return await apiClient.get('/admin/local-services/verified');
  },
  
  verifyAdminLocalService: async (serviceId, verifiedBy, verificationNotes) => {
    return await apiClient.post(`/admin/local-services/${serviceId}/verify`, null, {
      params: { verifiedBy, verificationNotes: verificationNotes || '' },
    });
  },
  
  unverifyAdminLocalService: async (serviceId, verifiedBy) => {
    return await apiClient.post(`/admin/local-services/${serviceId}/unverify`, null, {
      params: { verifiedBy },
    });
  },
  
  // Admin User Reports APIs
  getAdminAllReports: async () => {
    return await apiClient.get('/admin/reports/all');
  },
  
  getAdminPendingReports: async () => {
    return await apiClient.get('/admin/reports/pending');
  },
  
  getAdminReportsByStatus: async (status) => {
    return await apiClient.get(`/admin/reports/status/${status}`);
  },
  
  updateAdminReportStatus: async (reportId, status, reviewedBy, adminNotes) => {
    return await apiClient.post(`/admin/reports/${reportId}/status`, null, {
      params: { status, reviewedBy, adminNotes: adminNotes || '' },
    });
  },
  
  getAdminPendingReportsCount: async () => {
    return await apiClient.get('/admin/reports/pending/count');
  },
  
  // Admin Baby Names APIs
  getAdminBabyNameStatistics: async () => {
    return await apiClient.get('/admin/baby-names/statistics');
  },
  
  exportAdminBabyNamesCsv: async () => {
    const response = await apiClient.get('/admin/baby-names/export/csv', {
      responseType: 'blob',
    });
    return response;
  },
  
  exportAdminBabyNameStatisticsCsv: async () => {
    const response = await apiClient.get('/admin/baby-names/export/statistics/csv', {
      responseType: 'blob',
    });
    return response;
  },
  
  // Onboarding APIs
  completeOnboarding: async (onboardingData) => {
    return await apiClient.post('/onboarding/complete', onboardingData);
  },
  
  // Dashboard Analytics APIs
  getDashboardAnalytics: async (userId) => {
    return await apiClient.get(`/dashboard/analytics/${userId}`);
  },
  
  // Nutrition Insights APIs
  getNutritionInsights: async (userId) => {
    return await apiClient.get(`/nutrition/insights/${userId}`);
  },
  
  // User State APIs
  getUserState: async (userId) => {
    const cacheBust = { params: { _t: Date.now() } };
    if (userId) {
      try {
        return await apiClient.get(`/user/state/${userId}`, cacheBust);
      } catch (err) {
        console.log('⚠️ UserId-based state endpoint failed, trying token-based:', err);
      }
    }
    return await apiClient.get('/user/state', cacheBust);
  },
  getCurrentUserState: async () => {
    return await apiClient.get(`/user/state`);
  },
  getStateRequirements: async (state) => {
    return await apiClient.get(`/user/state/requirements/${state}`);
  },
  
  // TTC Profile APIs
  getTTCProfile: async (userId) => {
    return await apiClient.get(`/ttc/profile/${userId}`, { params: { _t: Date.now() } });
  },
  
  saveTTCProfile: async (ttcProfileData) => {
    return await apiClient.post('/ttc/profile', ttcProfileData);
  },
  
  // Smart Daily Summary — pass mode so backend returns content for current mode (pregnant/postpartum/trying_to_conceive)
  getSmartDailySummary: async (userId, includeCulturalFoods = false, mode = null) => {
    const params = {
      userId,
      includeCulturalFoods: includeCulturalFoods ? 'true' : 'false'
    };
    if (mode != null && String(mode).trim() !== '') params.mode = String(mode).trim();
    return await apiClient.get('/summary/daily', { params });
  },
  
  // Medical Sources
  getAllMedicalSources: async () => {
    return await apiClient.get('/medical-sources');
  },
  
  getMedicalSourceById: async (sourceId) => {
    return await apiClient.get(`/medical-sources/${sourceId}`);
  },
  
  getMedicalSourcesByCategory: async (category) => {
    return await apiClient.get(`/medical-sources/category/${category}`);
  },
  
  getMedicalSourcesByAuthority: async (authority) => {
    return await apiClient.get(`/medical-sources/authority/${authority}`);
  },
  
  getSourceCitation: async (sourceId, format) => {
    return await apiClient.get(`/medical-sources/${sourceId}/citation`, {
      params: { format },
    });
  },
  
  getRecommendedSources: async (topic) => {
    return await apiClient.get(`/medical-sources/recommended/${topic}`);
  },
  
  // Health Profile Summary
  getHealthProfileSummary: async (userId) => {
    try {
      return await apiClient.get(`/health-profile/summary/${userId}`, { params: { _t: Date.now() } });
    } catch (error) {
      // Health profile is optional - return null if not found
      // Silently handle 404s - no logging needed
      if (error.status === 404 || error.errorCode === 'NOT_FOUND') {
        return null;
      }
      throw error;
    }
  },
  
  // Notification APIs
  getNotificationPreferences: async (userId) => {
    return await apiClient.get(`/notifications/preferences/user/${userId}`);
  },
  
  saveNotificationPreferences: async (preferences) => {
    return await apiClient.post('/notifications/preferences', preferences);
  },
  
  markNotificationAsRead: async (notificationId) => {
    return await apiClient.post(`/notifications/${notificationId}/read`);
  },
  
  markAllNotificationsAsRead: async (userId) => {
    return await apiClient.post(`/notifications/user/${userId}/read-all`);
  },
  
  getUnreadNotificationCount: async (userId) => {
    return await apiClient.get(`/notifications/user/${userId}/unread-count`);
  },
  
  cancelNotification: async (notificationId) => {
    return await apiClient.post(`/notifications/${notificationId}/cancel`);
  },
  
  deleteNotification: async (notificationId) => {
    return await apiClient.delete(`/notifications/${notificationId}`);
  },
  
  deleteAllReadNotifications: async (userId) => {
    return await apiClient.delete(`/notifications/user/${userId}/read`);
  },

  // Blog APIs (public)
  getBlogPosts: async () => {
    return await apiClient.get('/blog/posts');
  },

  getBlogPostBySlug: async (slug) => {
    return await apiClient.get(`/blog/posts/${slug}`);
  },

  // Blog APIs (admin)
  adminGetBlogPosts: async () => {
    return await apiClient.get('/admin/blog/posts');
  },

  adminCreateBlogPost: async (payload) => {
    return await apiClient.post('/admin/blog/posts', payload);
  },

  adminUpdateBlogPost: async (id, payload) => {
    return await apiClient.put(`/admin/blog/posts/${id}`, payload);
  },

  adminDeleteBlogPost: async (id) => {
    return await apiClient.delete(`/admin/blog/posts/${id}`);
  },
  
  // Postpartum APIs
  getPostpartumGuidance: async (weeksPostpartum) => {
    return await apiClient.get('/postpartum/guidance', {
      params: { weeksPostpartum }
    });
  },
};

